import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from '@itsrever/design-system'

export interface SettingsMutation<Input> {
  mutationFn: (input: Input) => Promise<unknown>
  // The query of the setting the save writes: it must have loaded, and reloads once saved.
  invalidate: QueryKey
  successKey: string
  errorKey: string
  onSuccess?: (input: Input) => void
}

// One Configuration save: it toasts the outcome and reloads the setting it wrote.
export function useSettingsMutation<Input>({
  mutationFn,
  invalidate,
  successKey,
  errorKey,
  onSuccess
}: SettingsMutation<Input>) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn,
    // Awaited: until the saved value is back, a second Save would send the same edits again.
    onSuccess: async (_, input) => {
      onSuccess?.(input)
      toast({ text: t(successKey), variant: 'success' })
      await queryClient.invalidateQueries({ queryKey: invalidate })
    },
    onError: (error) => {
      console.warn(error)
      toast({ text: t(errorKey), variant: 'error' })
    }
  })

  // Resolves to whether the save went through, so a caller can keep what only it holds.
  async function save(input: Input): Promise<boolean> {
    // Edits over a setting that never loaded could replace everything stored with themselves.
    if (queryClient.getQueryData(invalidate) === undefined) return false
    return mutateAsync(input).then(
      () => true,
      () => false
    )
  }

  return { save, saving: isPending }
}
